import { z } from 'zod';
import { RoleIdSchema } from './common';
import { RoleLabel } from './users';
import { PermissionSchema, type Permission } from './permissions';

/** Los 6 roles built-in (no se pueden borrar). */
export const BUILTIN_ROLE_IDS = [
  'admin',
  'medico',
  'anestesiologo',
  'enfermera',
  'recepcion',
  'auditoria',
] as const;
export type BuiltinRoleId = (typeof BUILTIN_ROLE_IDS)[number];

export function isBuiltinRole(id: string): id is BuiltinRoleId {
  return (BUILTIN_ROLE_IDS as readonly string[]).includes(id);
}

/**
 * Permisos EXACTOS de cada rol built-in — replican 1:1 el acceso que tenían con
 * los guards `@Roles(...)` antes de la migración a permisos. NO cambiar sin
 * revisar el inventario de endpoints (cualquier cambio altera accesos).
 */
export const BUILTIN_ROLE_PERMISSIONS: Record<BuiltinRoleId, readonly Permission[]> = {
  admin: ['*'],
  auditoria: [
    'anesthesia:read',
    'audit:read',
    'catalog:read',
    'clinical-docs:read',
    'consent:read',
    'dashboard:read',
    'expense:read',
    'pathology:read',
    'patients:read',
    'procedures:read',
    'reports:read',
    'results:read',
    'users:read',
  ],
  medico: [
    'anesthesia:read',
    'anesthesia:sign',
    'catalog:read',
    'clinical-docs:read',
    'consent:read',
    'consent:sign',
    'dashboard:read',
    'expense:read',
    'pathology:read',
    'pathology:sign',
    'patients:read',
    'patients:portal-credentials',
    'patients:write',
    'procedures:read',
    'procedures:write',
    'procedures:transition',
    'procedures:patient-status',
    'procedures:cancel',
    'reports:read',
    'results:read',
    'results:write',
    'results:delete',
    'users:read-firma',
  ],
  anestesiologo: [
    'anesthesia:read',
    'anesthesia:sign',
    'catalog:read',
    'clinical-docs:read',
    'consent:read',
    'dashboard:read',
    'expense:read',
    'pathology:read',
    'patients:read',
    'procedures:read',
    'procedures:transition',
    'results:read',
    'users:read-firma',
  ],
  enfermera: [
    'anesthesia:read',
    'anesthesia:sign',
    'catalog:read',
    'clinical-docs:read',
    'consent:read',
    'dashboard:read',
    'expense:read',
    'expense:sign',
    'pathology:read',
    'pathology:sign',
    'patients:read',
    'patients:portal-credentials',
    'procedures:read',
    'procedures:transition',
    'procedures:patient-status',
    'payroll:read',
    'payroll:clock',
    'results:read',
    'results:write',
  ],
  recepcion: [
    'anesthesia:read',
    'catalog:read',
    'clinical-docs:read',
    'consent:read',
    'dashboard:read',
    'expense:read',
    'pathology:read',
    'pathology:sign',
    'patients:read',
    'patients:portal-credentials',
    'patients:write',
    'procedures:read',
    'procedures:write',
    'reports:read',
    'results:read',
  ],
};

/** Descripción corta de cada rol built-in (para el listado de perfiles). */
export const BUILTIN_ROLE_DESCRIPTIONS: Record<BuiltinRoleId, string> = {
  admin: 'Acceso total a la plataforma.',
  medico: 'Atiende procedimientos, firma consentimientos y sube resultados.',
  anestesiologo: 'Firma registros de anestesia y consulta episodios.',
  enfermera: 'Firma hoja de gasto y patología; registra turnos de nómina.',
  recepcion: 'Admisión de pacientes y creación de procedimientos.',
  auditoria: 'Solo lectura, incluida la bitácora de auditoría.',
};

/**
 * Registro de un rol/perfil. Los built-in tienen `esSistema=true` (no editables
 * en su identidad ni borrables). Los personalizados los crea el admin.
 */
export const RoleProfileSchema = z.object({
  id: RoleIdSchema,
  nombre: z.string().min(2).max(60),
  descripcion: z.string().max(200).optional(),
  permisos: z.array(PermissionSchema),
  esSistema: z.boolean(),
});
export type RoleProfile = z.infer<typeof RoleProfileSchema>;

/** Crear un perfil personalizado: nombre + permisos (el id se deriva del nombre). */
export const CreateRoleProfileSchema = z.object({
  nombre: z.string().min(2, 'Nombre muy corto').max(60),
  descripcion: z.string().max(200).optional(),
  permisos: z.array(PermissionSchema).min(1, 'Selecciona al menos un permiso'),
});
export type CreateRoleProfileInput = z.infer<typeof CreateRoleProfileSchema>;

/** Editar un perfil personalizado (nombre/descripcion/permisos). El id no cambia. */
export const UpdateRoleProfileSchema = CreateRoleProfileSchema.partial();
export type UpdateRoleProfileInput = z.infer<typeof UpdateRoleProfileSchema>;

/** Los 6 perfiles built-in materializados (para seed y fallback de etiquetas). */
export const BUILTIN_ROLES: readonly RoleProfile[] = BUILTIN_ROLE_IDS.map((id) => ({
  id,
  nombre: RoleLabel[id],
  descripcion: BUILTIN_ROLE_DESCRIPTIONS[id],
  permisos: [...BUILTIN_ROLE_PERMISSIONS[id]],
  esSistema: true,
}));
